import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { z } from "zod";
import type { PlanStep, WorkPlan } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { useDraftMaterialsBlocked } from "./draft-materials.tsx";
import { refusalOf } from "./material-request-sheet.tsx";
import { usePlanApi } from "./workspace-context.tsx";

/*
 * The commitment slots of a core v2 draft (`src/draft-slots.ts`): the seller commitments only the
 * rep can make, such as a time or who joins. The draft shown elsewhere is the reading that commits
 * to nothing; here each slot sits where it goes in the reply, with its proposed text editable, an
 * input for each blank it asks the rep to fill, and the choice to use the fallback instead. Until
 * every slot is confirmed, the editor is locked and Approve is off; the server refuses approval
 * anyway (409 `slots_unresolved`). Confirming writes one ordinary edit whose body is the result.
 */

const SlotSchema = z.object({
  id: z.string(),
  kind: z.string(),
  proposedText: z.string(),
  fallbackText: z.string(),
  dependsOn: z.string(),
  fills: z.array(z.object({ name: z.string(), ask: z.string() })),
});
type Slot = z.infer<typeof SlotSchema>;
const DraftSlotsSchema = z.object({
  state: z.enum(["none", "unresolved", "resolved"]),
  generatedRevision: z.number().nullable(),
  segments: z.array(
    z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("text"), text: z.string() }),
      z.object({ kind: z.literal("slot"), id: z.string() }),
    ]),
  ),
  slots: z.array(SlotSchema),
});
type DraftSlotsView = z.infer<typeof DraftSlotsSchema>;

const draftSlotsKey = (planId: string, revision: number) =>
  ["draft-slots", planId, revision] as const;

/** A server without the route (an older build) answers 404: a draft with no slot. */
async function readDraftSlots(planApi: string, revision: number): Promise<DraftSlotsView | null> {
  const response = await fetch(`${planApi}/drafts/${revision}/slots`, {
    credentials: "same-origin",
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`draft_slots_${response.status}`);
  return DraftSlotsSchema.parse(await response.json());
}

/**
 * Whether a draft revision waits on its slots, for every reply step on the page at once, from the
 * same cache as the slots themselves. Until the read settles nothing is blocked: the server is the
 * gate, as it is for a draft's file (`draft-materials.tsx`).
 */
function useDraftSlotsOpen(
  planId: string,
  revisions: readonly number[],
): (revision: number) => boolean {
  const planApi = usePlanApi(planId);
  const unique = [...new Set(revisions)];
  const results = useQueries({
    queries: unique.map((revision) => ({
      queryKey: draftSlotsKey(planId, revision),
      queryFn: () => readDraftSlots(planApi, revision),
    })),
  });
  return (revision: number) => {
    const index = unique.indexOf(revision);
    return index >= 0 && results[index]?.data?.state === "unresolved";
  };
}

/** Said in the reply box where the agent's draft would be offered while its slots wait (`agent-item.tsx`). */
export const SLOTS_WAIT_LINE =
  "This reply commits to something only you can confirm; confirm it on the agent's card first.";

/**
 * Whether this draft revision waits on its commitment slots, from the slots' own cache. The reply box
 * offers the draft only once they are confirmed: its text holds the times and names not yet agreed.
 */
export function useSlotsWaiting(planId: string, revision: number | null): boolean {
  const open = useDraftSlotsOpen(planId, revision === null ? [] : [revision]);
  return revision !== null && open(revision);
}

/**
 * What keeps a reply step's draft from approval besides the rep's own state, for the Approve gate
 * (`src/approve-gate.ts`): its file (`draft-materials.tsx`) and its commitment slots. False for any
 * other step and for a step whose draft is not open.
 */
export function useDraftReadiness(
  plan: WorkPlan,
): (step: PlanStep) => { materialBlocked: boolean; slotsUnresolved: boolean } {
  const revisions = plan.steps.flatMap((step) =>
    step.input.kind === "reply" && ["pending", "invalidated", "approved"].includes(step.state)
      ? [step.input.draftRevision]
      : [],
  );
  const materialBlocked = useDraftMaterialsBlocked(plan.id, revisions);
  const slotsOpen = useDraftSlotsOpen(plan.id, revisions);
  return (step) =>
    step.input.kind === "reply"
      ? {
          materialBlocked: materialBlocked(step.input.draftRevision),
          slotsUnresolved: slotsOpen(step.input.draftRevision),
        }
      : { materialBlocked: false, slotsUnresolved: false };
}

/**
 * A reply step's body, or its slots in its place while they wait on the rep: the editor stays
 * locked until every slot is confirmed. A conversation the app holds read-only keeps its body.
 */
export function SlotsInPlace(props: {
  plan: WorkPlan;
  step: PlanStep;
  readiness: (step: PlanStep) => { slotsUnresolved: boolean };
  locked: boolean;
  onChange: () => Promise<void>;
  children: ReactNode;
}) {
  const { step } = props;
  const open = props.readiness(step).slotsUnresolved;
  if (!open || props.locked || step.input.kind !== "reply") return props.children;
  return (
    <DraftSlots
      planId={props.plan.id}
      planRevision={props.plan.revision}
      stepId={step.id}
      draftRevision={step.input.draftRevision}
      onChange={props.onChange}
    />
  );
}

/** What the rep has chosen for one slot so far. */
type Choice = { use: "proposed" | "fallback"; text: string };

const BLANK = /\[\[fill:([a-z_]+)\]\]/g;
const blanksIn = (text: string): string[] => [...text.matchAll(BLANK)].flatMap((m) => m[1] ?? []);

/** The blanks a confirmed slot still needs: those its current wording names, once each. */
function neededBlanks(slot: Slot, choice: Choice): Slot["fills"] {
  if (choice.use === "fallback") return [];
  const named = new Set(blanksIn(choice.text));
  return [
    ...slot.fills.filter((fill) => named.has(fill.name)),
    ...[...named]
      .filter((name) => !slot.fills.some((fill) => fill.name === name))
      .map((name) => ({ name, ask: name.replaceAll("_", " ") })),
  ];
}

function refusalText(code: string | null): string {
  switch (code) {
    case "slot_fill_missing":
      return "Fill in every blank, or use the fallback for that part.";
    case "slot_text_empty":
      return "A confirmed part cannot be empty. Write it, or use the fallback.";
    case "slot_marker_left":
      return "Remove the {{ }} or [[ ]] markers from your text; they would be sent as written.";
    case "slots_already_resolved":
    case "stale_revision":
      return "This draft changed since you opened it. Refresh to see it.";
    default:
      return `Your choices could not be saved (${code ?? "error"}). Nothing changed.`;
  }
}

/** One slot where it goes in the reply. */
function SlotCard(props: {
  slot: Slot;
  index: number;
  choice: Choice;
  fills: Record<string, string>;
  busy: boolean;
  onChoice: (choice: Choice) => void;
  onFill: (name: string, value: string) => void;
}) {
  const { slot, choice } = props;
  const label = `Part ${props.index + 1} to confirm`;
  return (
    <fieldset className="lcr-slot" data-use={choice.use} aria-label={label}>
      <p className="lcr-slot-head">
        <span className="lcr-slot-title">{label}</span>
        <span className="lcr-slot-depends">{slot.dependsOn}</span>
      </p>
      {choice.use === "proposed" ? (
        <>
          <textarea
            className="lcr-slot-text"
            aria-label={`${label}: the text you confirm`}
            value={choice.text}
            rows={2}
            disabled={props.busy}
            onChange={(event) => props.onChoice({ ...choice, text: event.target.value })}
          />
          {neededBlanks(slot, choice).map((fill) => (
            <label key={fill.name} className="lcr-slot-fill">
              <span>{fill.ask}</span>
              <input
                type="text"
                className="lcr-slot-input"
                value={props.fills[fill.name] ?? ""}
                disabled={props.busy}
                onChange={(event) => props.onFill(fill.name, event.target.value)}
              />
            </label>
          ))}
        </>
      ) : (
        <p className="lcr-slot-fallback">
          {slot.fallbackText ? slot.fallbackText : "This part is left out of the reply."}
        </p>
      )}
      <label className="lcr-slot-toggle">
        <input
          type="checkbox"
          checked={choice.use === "fallback"}
          disabled={props.busy}
          onChange={(event) =>
            props.onChoice({ ...choice, use: event.target.checked ? "fallback" : "proposed" })
          }
        />
        <span>
          Use the fallback instead
          {slot.fallbackText ? "" : " (leaves this part out)"}
        </span>
      </label>
    </fieldset>
  );
}

/** Whether every slot can be sent as chosen: a confirmed text with every blank filled. */
function complete(
  view: DraftSlotsView,
  choices: Record<string, Choice>,
  fills: Record<string, string>,
) {
  return view.slots.every((slot) => {
    const choice = choices[slot.id];
    if (!choice) return false;
    if (choice.use === "fallback") return true;
    return (
      choice.text.trim().length > 0 &&
      neededBlanks(slot, choice).every((fill) => (fills[fill.name] ?? "").trim().length > 0)
    );
  });
}

/** The slots with their data, for one reply step's draft revision; nothing once resolved. */
function DraftSlots(props: {
  planId: string;
  planRevision: number;
  stepId: string;
  draftRevision: number;
  onChange: () => Promise<void>;
}) {
  const planApi = usePlanApi(props.planId);
  const cache = useQueryClient();
  const view = useQuery({
    queryKey: draftSlotsKey(props.planId, props.draftRevision),
    queryFn: () => readDraftSlots(planApi, props.draftRevision),
  });
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [fills, setFills] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = view.data;
  if (data?.state !== "unresolved") return null;
  const choiceFor = (slot: Slot): Choice =>
    choices[slot.id] ?? { use: "proposed", text: slot.proposedText };
  const current = Object.fromEntries(data.slots.map((slot) => [slot.id, choiceFor(slot)]));
  const ready = complete(data, current, fills);
  const byId = new Map(data.slots.map((slot) => [slot.id, slot]));
  const submit = () => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const response = await fetch(`${planApi}/slots`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            commandId: crypto.randomUUID(),
            expectedRevision: props.planRevision,
            stepId: props.stepId,
            choices: data.slots.map((slot) => {
              const choice = choiceFor(slot);
              return choice.use === "fallback"
                ? { id: slot.id, use: "fallback" }
                : { id: slot.id, use: "proposed", text: choice.text };
            }),
            fills,
          }),
        });
        if (!response.ok) throw new Error(refusalText(await refusalOf(response)));
        await cache.invalidateQueries({ queryKey: ["draft-slots", props.planId] });
        await props.onChange();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Your choices could not be saved.");
      } finally {
        setBusy(false);
      }
    })();
  };
  return (
    <section className="lcr-slots" aria-label="Commitments to confirm">
      <p className="lcr-slots-intro">
        This reply commits to {data.slots.length === 1 ? "something" : "things"} only you can
        confirm. Confirm or change each part, or use its fallback. Approve stays off until you do.
      </p>
      <div className="lcr-slots-body">
        {data.segments.map((segment, index) => {
          // Only the space between two slots: the grid's gap already separates them.
          if (segment.kind === "text" && !segment.text.trim()) return null;
          if (segment.kind === "text")
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: segments are a fixed template's order.
              <span key={index} className="lcr-slots-text">
                {segment.text}
              </span>
            );
          const slot = byId.get(segment.id);
          if (!slot) return null;
          return (
            <SlotCard
              key={slot.id}
              slot={slot}
              index={data.slots.indexOf(slot)}
              choice={choiceFor(slot)}
              fills={fills}
              busy={busy}
              onChoice={(choice) => setChoices((before) => ({ ...before, [slot.id]: choice }))}
              onFill={(name, value) => setFills((before) => ({ ...before, [name]: value }))}
            />
          );
        })}
      </div>
      {error && (
        <p className="lcr-slots-error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="lcr-primary lcr-slots-confirm"
        disabled={busy || !ready}
        onClick={submit}
      >
        {busy ? "Saving…" : data.slots.length === 1 ? "Confirm this part" : "Confirm these parts"}
      </button>
    </section>
  );
}
